import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { AuthToken, Role, User } from '../types';
import * as usersApi from '../api/users';
import * as authApi from '../api/auth';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const THROTTLE_INTERVAL_MS = 60 * 1000;

interface AuthState {
  isAuthenticated: boolean;
  role: Role | null;
  accessToken: string | null;
  me: User | null;
}

interface AuthContextType extends AuthState {
  loginWithToken: (token: AuthToken) => void;
  logout: () => void;
  refreshMe: () => Promise<void>;
  setMe: (user: User) => void;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  role: null,
  accessToken: null,
  me: null,
  loginWithToken: () => {},
  logout: () => {},
  refreshMe: async () => {},
  setMe: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(() => {
    const token = localStorage.getItem('access_token');
    const role = localStorage.getItem('role') as Role | null;
    return {
      isAuthenticated: !!token,
      role,
      accessToken: token,
      me: null,
    };
  });

  const refreshMe = useCallback(async () => {
    try {
      const user = await usersApi.getMe();
      setState((prev) => ({ ...prev, me: user, role: user.role }));
      localStorage.setItem('role', user.role);
    } catch {
      // 401 등은 api/client.ts 인터셉터가 처리
    }
  }, []);

  const setMe = useCallback((user: User) => {
    setState((prev) => ({ ...prev, me: user, role: user.role }));
    localStorage.setItem('role', user.role);
  }, []);

  const loginWithToken = useCallback((token: AuthToken) => {
    localStorage.setItem('access_token', token.access_token);
    localStorage.setItem('refresh_token', token.refresh_token);
    localStorage.setItem('role', token.role);
    setState({
      isAuthenticated: true,
      role: token.role,
      accessToken: token.access_token,
      me: null,
    });
  }, []);

  const logout = useCallback(() => {
    // ISS-029: 서버 측 감사 로그(LOGOUT) 기록을 위한 fire-and-forget 호출.
    // 네트워크/서버 어떤 오류에서도 클라이언트 측 로그아웃(localStorage 정리 + state reset)은 반드시 완료되어야 한다.
    authApi.logout().catch(() => { /* 토큰 만료·네트워크 끊김 등 무시 */ });
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('role');
    setState({ isAuthenticated: false, role: null, accessToken: null, me: null });
  }, []);

  // 로그인 직후 또는 새로고침 복원 시 /me 프로필 동기화
  useEffect(() => {
    if (state.isAuthenticated && state.accessToken && !state.me) {
      refreshMe();
    }
  }, [state.isAuthenticated, state.accessToken, state.me, refreshMe]);

  // ISS-069 — axios 인터셉터/SSE 가 토큰을 silent refresh 하면 그 결과를 state 에 동기화한다.
  // 동기화하지 않으면 state.accessToken 이 로그인 시점 토큰에 고정되어
  // SSEContext 가 만료된 옛 토큰으로 재연결을 시도한다 (실시간 연결 간헐 끊김).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ access_token?: string; role?: string }>).detail;
      if (!detail?.access_token) return;
      setState((prev) => ({
        ...prev,
        isAuthenticated: true,
        accessToken: detail.access_token as string,
        role: (detail.role as Role) || prev.role,
      }));
    };
    window.addEventListener('auth:token-refreshed', handler);
    return () => window.removeEventListener('auth:token-refreshed', handler);
  }, []);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!state.isAuthenticated) return;

    const resetTimer = () => {
      lastActivityRef.current = Date.now();
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, INACTIVITY_TIMEOUT_MS);
    };

    let throttleTimeout: ReturnType<typeof setTimeout> | null = null;
    const throttledReset = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        resetTimer();
      }, THROTTLE_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= INACTIVITY_TIMEOUT_MS) {
          logout();
          window.location.href = '/login';
        } else {
          resetTimer();
        }
      }
    };

    const events: (keyof WindowEventMap)[] = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(e => window.addEventListener(e, throttledReset));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    resetTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, throttledReset));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [state.isAuthenticated, logout]);

  return (
    <AuthContext.Provider value={{ ...state, loginWithToken, logout, refreshMe, setMe }}>
      {children}
    </AuthContext.Provider>
  );
};
